import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import FlipCameraIosOutlinedIcon from '@mui/icons-material/FlipCameraIosOutlined';
import LogDateField from '../UI/LogDateField';
import LogTimeField from '../UI/LogTimeField';
import LogDatePickerPopover from '../UI/LogDatePickerPopover';
import ImageCaptureIntentSelector from '../../features/imageCapture/ImageCaptureIntentSelector';

const formatSheetDateValue = (dateValue) => {
  if (!(dateValue instanceof Date) || Number.isNaN(dateValue.getTime())) {
    return 'Select date';
  }

  return dateValue.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });
};

const QuickCheckInCaptureFields = ({
  selectedDate,
  selectedTime,
  onSelectedDateChange,
  onSelectedTimeChange,
  photoInputRef,
  photoFile,
  photoDisplayName = '',
  photoPreviewUrl,
  onPhotoSelect,
  onRemovePhoto,
  photoIntent,
  onPhotoIntentChange,
  cameraOpen = false,
  cameraError = '',
  videoRef,
  canvasRef,
  onCameraClick,
  onFlipCamera,
  onCapturePhoto,
  onRecordVoice,
  children,
}) => {
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);

  const handleDatePickerOpen = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (nextDate) => {
    if (!nextDate) {
      return;
    }

    onSelectedDateChange(nextDate);
    setDatePickerAnchor(null);
  };

  return (
    <>
      <Box sx={{ mb: 2, position: 'relative' }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{
            width: '100%',
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ flex: 1.05, minWidth: 0 }}>
            <LogDateField
              label="Date"
              value={formatSheetDateValue(selectedDate)}
              onClick={handleDatePickerOpen}
            />
          </Box>

          <Box sx={{ flex: 0.95, minWidth: 0 }}>
            <LogTimeField
              value={selectedTime}
              onChange={(event) => onSelectedTimeChange(event.target.value)}
              label="Time"
            />
          </Box>
        </Stack>
        <LogDatePickerPopover
          open={Boolean(datePickerAnchor)}
          anchorEl={datePickerAnchor}
          onClose={handleDatePickerClose}
          value={selectedDate}
          onChange={handleDateChange}
        />
      </Box>

      {children}

      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          onClick={onRecordVoice}
          sx={{ textTransform: 'none', borderRadius: '999px', borderColor: '#cfdad5', color: '#2f4b42', bgcolor: '#f7fbf8', fontWeight: 700, px: 1.8 }}
          startIcon={<MicRoundedIcon />}
        >
          Voice
        </Button>
        <Button
          variant="outlined"
          onClick={() => photoInputRef.current?.click()}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            borderColor: '#cfdad5',
            color: '#2f4b42',
            bgcolor: '#f7fbf8',
            fontWeight: 700,
            px: 1.8,
            '&:hover': {
              bgcolor: '#eef6f2',
              borderColor: '#bfd4cb',
            },
          }}
          startIcon={<AddPhotoAlternateIcon />}
        >
          Add photo
        </Button>
        <Button
          variant="outlined"
          onClick={onCameraClick}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            borderColor: '#cfdad5',
            color: '#2f4b42',
            bgcolor: '#f7fbf8',
            fontWeight: 700,
            px: 1.8,
            '&:hover': {
              bgcolor: '#eef6f2',
              borderColor: '#bfd4cb',
            },
          }}
          startIcon={<PhotoCameraIcon />}
        >
          {cameraOpen ? 'Close camera' : 'Take photo'}
        </Button>
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={onPhotoSelect}
        />
        {photoFile ? (
          <Chip
            label={photoDisplayName || photoFile.name}
            onDelete={onRemovePhoto}
            sx={{ borderRadius: '999px' }}
          />
        ) : null}
      </Box>

      {photoPreviewUrl ? (
        <>
        <Box
          sx={{
            mt: 1,
            width: '100%',
            maxWidth: 280,
            minHeight: 140,
            borderRadius: '18px',
            overflow: 'hidden',
            border: '1px solid #dce5df',
            bgcolor: '#f8fbf9',
          }}
        >
          <Box
            component="img"
            src={photoPreviewUrl}
            alt={photoFile?.name || 'Selected photo preview'}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />
        </Box>
        <ImageCaptureIntentSelector value={photoIntent} onChange={onPhotoIntentChange} />
        </>
      ) : null}

      {cameraOpen ? (
        <Box
          sx={{
            mt: 1.25,
            width: '100%',
            borderRadius: '18px',
            overflow: 'hidden',
            border: '1px solid #dce5df',
            bgcolor: '#111827',
            position: 'relative',
            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
          }}
        >
          <IconButton
            onClick={onFlipCamera}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              bgcolor: 'rgba(17, 24, 39, 0.78)',
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(17, 24, 39, 0.92)',
              },
            }}
          >
            <FlipCameraIosOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Box
            component="video"
            ref={videoRef}
            autoPlay
            playsInline
            muted
            sx={{
              width: '100%',
              display: 'block',
              aspectRatio: '4 / 3',
              objectFit: 'cover',
              backgroundColor: '#111827',
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1.25, bgcolor: '#fff' }}>
            <Button
              variant="contained"
              onClick={onCapturePhoto}
              sx={{
                textTransform: 'none',
                borderRadius: '999px',
                px: 2.25,
                fontWeight: 700,
                bgcolor: '#3f7f6b',
                '&:hover': {
                  bgcolor: '#356b5a',
                },
              }}
            >
              Capture
            </Button>
          </Box>
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </Box>
      ) : null}

      {cameraError ? (
        <Typography sx={{ mt: 0.85, color: 'error.main', fontSize: '0.78rem', fontWeight: 600 }}>
          {cameraError}
        </Typography>
      ) : null}
    </>
  );
};

export default QuickCheckInCaptureFields;
